import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { ArrowLeft, Save, Eye, EyeOff, User, Mail, Phone, Shield, Calendar } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { PhoneInput } from '@/components/ui/phone-input';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { User as UserType } from '@/types/auth';
import {
  useAdminUsers,
  useUpdateAdminUser,
  useActivateAdminUser,
  useDeactivateAdminUser
} from '@/api-services/hooks/useAdminUsers';
import { useRolesDropdown } from '@/api-services/hooks/useRoles';
import { useAdminResetUserPassword } from '@/api-services/hooks/useAuth';
import { UserDTO } from '@/api-services/hooks/adminTypes';
import { parsePhoneNumberFromString } from 'libphonenumber-js';
import { useDuplicateCheck } from '@/hooks/useDuplicateCheck';

interface EditUserForm {
  userName: string;
  firstName: string;
  lastName: string;
  email: string;
  mobile: string;
  phoneCountryCode: string;
  password: string;
  confirmPassword: string;
  roles: string[];
  rolesAssigned: string;
  status: string;
  activated: boolean;
}

const EditUserPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { userId } = useParams<{ userId: string }>();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [userDTO, setUserDTO] = useState<UserDTO | null>(null);
  const [fetchingUser, setFetchingUser] = useState(true);
  const { duplicateErrors, checkEmail, checkPhone } = useDuplicateCheck('admin', userId);
  const emailRef = React.useRef<HTMLInputElement>(null);
  const phoneRef = React.useRef<HTMLDivElement>(null);

  // Fetch roles dropdown list from API
  const { data: rolesData, loading: rolesLoading } = useRolesDropdown({
    onError: (error) => {
      console.error('Failed to fetch roles:', error);
    },
  });
  // Handle both string array and object array formats
  const rolesDropdown = Array.isArray(rolesData)
    ? rolesData.map(r => typeof r === 'string' ? { roleName: r, roleId: r } : r)
    : [];

  const [formData, setFormData] = useState<EditUserForm>({
    userName: '',
    firstName: '',
    lastName: '',
    email: '',
    mobile: '',
    phoneCountryCode: '',
    password: '',
    confirmPassword: '',
    roles: ['ROLE_USER'],
    rolesAssigned: '',
    status: 'active',
    activated: true,
  });

  // Check if user data was passed via navigation state
  const passedUser = location.state?.user as UserType | undefined;
  const passedDTO = location.state?.userDTO as UserDTO | undefined;

  // Convert UI User to UserDTO format
  const convertUserToDTO = (user: UserType): UserDTO => {
    const userId = user.companyUserId || parseInt(user.id);
    return {
      id: userId,
      companyUserId: userId,
      userName: user.userName || user.email.split('@')[0],
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      mobile: user.phone || user.phoneNumber,
      roles: user.roles,
      rolesAssigned: user.rolesAssigned,
      status: user.status,
      activated: user.activated,
    };
  };

  // Fetch user data from API as fallback
  const usersQuery = useAdminUsers(
    {
      page: 0,
      size: 1000, // Get all users to find the specific one
    },
    {
      enabled: !passedDTO && !passedUser && !!userId, // Only fetch if no user data was passed
      onSuccess: (data) => {
        console.log('Fetched users data:', data);
        
        // Find the user with matching ID
        const foundUser = data.content.find(
          (u) => String(u.id) === userId || String(u.companyUserId) === userId
        );
        
        if (foundUser) {
          console.log('Found user:', foundUser);
          setUserDTO(foundUser);
          
          // Map API data to form
          const isActivated = foundUser.activated !== false;
          const fullNumber = foundUser.phoneCountryCode && foundUser.mobile
  ? `${foundUser.phoneCountryCode}${foundUser.mobile}`
  : foundUser.mobile || '';

const phone = parsePhoneNumberFromString(fullNumber);
          setFormData({
            userName: foundUser.userName || '',
            firstName: foundUser.firstName || '',
            lastName: foundUser.lastName || '',
            email: foundUser.email || '',
            //mobile: foundUser.mobile || '',
            mobile: phone ? phone.number : fullNumber, // ✅ E.164 format
            phoneCountryCode: foundUser.phoneCountryCode || '', // ✅ keep it
            password: '',
            confirmPassword: '',
            roles: ['ROLE_USER'],
            rolesAssigned: foundUser.rolesAssigned || '',
            status: isActivated ? 'active' : 'inactive',
            activated: isActivated,
          });
          setFetchingUser(false);
        } else {
          console.error('User not found in list');
          toast.error('User not found');
          navigate('/admin/users/list');
        }
      },
      onError: (error) => {
        console.error('Failed to fetch users:', error);
        toast.error('Failed to fetch user details');
        navigate('/admin/users/list');
      },
    }
  );

  // Load user data from passed state
  useEffect(() => {
    if (passedDTO || passedUser) {
      // Prefer the original UserDTO from API (has all metadata fields)
      const dto = passedDTO || (passedUser ? convertUserToDTO(passedUser) : null);
      if (!dto) return;

      console.log('Using passed user data:', dto);
      setUserDTO(dto);
const fullNumber = dto.phoneCountryCode && dto.mobile
  ? `${dto.phoneCountryCode}${dto.mobile}`
  : dto.mobile || '';

const phone = parsePhoneNumberFromString(fullNumber);

      const isActivated = dto.activated !== false;
      setFormData({
        userName: dto.userName || '',
        firstName: dto.firstName || '',
        lastName: dto.lastName || '',
        email: dto.email || '',
mobile: phone ? phone.number : fullNumber, // ✅ E.164 format
phoneCountryCode: dto.phoneCountryCode || '',
        password: '',
        confirmPassword: '',
        roles: ['ROLE_USER'],
        rolesAssigned: dto.rolesAssigned || '',
        status: isActivated ? 'active' : 'inactive',
        activated: isActivated,
      });
      setFetchingUser(false);
    }
  }, [passedDTO, passedUser]);

  // Update user mutation
const updateUserMutation = useUpdateAdminUser({
  onSuccess: (response) => {
    if (response.status === "failed") {
      toast.error(response.message);
      return;
    }

    toast.success(response.message || "User updated successfully!");
    navigate('/admin/users/list');
  },
  onError: (error) => {
    toast.error(error.message || "Failed to update user");
  }
});

  // Password change mutation (uses dedicated password API endpoint)
  const changePasswordMutation = useAdminResetUserPassword();

  // Activate user mutation
  const activateUserMutation = useActivateAdminUser({
    onSuccess: () => {
      toast.success('User activated successfully!');
      // Update form state to reflect the change
      setFormData(prev => ({ ...prev, status: 'active', activated: true }));
    },
    onError: (error) => {
      console.error('Failed to activate user:', error);
      toast.error(`Failed to activate user: ${error.message}`);
      // Revert the status change on error
      setFormData(prev => ({ ...prev, status: 'inactive', activated: false }));
    },
  });

  // Deactivate user mutation
  const deactivateUserMutation = useDeactivateAdminUser({
    onSuccess: () => {
      toast.success('User deactivated successfully!');
      // Update form state to reflect the change
      setFormData(prev => ({ ...prev, status: 'inactive', activated: false }));
    },
    onError: (error) => {
      console.error('Failed to deactivate user:', error);
      toast.error(`Failed to deactivate user: ${error.message}`);
      // Revert the status change on error
      setFormData(prev => ({ ...prev, status: 'active', activated: true }));
    },
  });

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.userName.trim()) {
      newErrors.userName = 'Username is required';
    }

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Invalid email format';
    }

    if (!formData.mobile.trim()) {
      newErrors.mobile = 'Phone number is required';
    } else if (formData.mobile.length > 15) {
      newErrors.mobile = 'Phone number must be 10 digits';
    } else if (!isValidPhoneNumber(formData.mobile)) {
      newErrors.mobile = 'Invalid phone number';
    }

    // Only validate password if user is trying to change it
    if (formData.password || formData.confirmPassword) {
      if (formData.password && formData.password.length < 8) {
        newErrors.password = 'Password must be at least 8 characters long';
      }

      if (formData.password && !formData.confirmPassword) {
        newErrors.confirmPassword = 'Please confirm new password';
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
    }

    if (!formData.roles || formData.roles.length === 0) {
      newErrors.roles = 'At least one role is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error('Please fix the validation errors');
      return;
    }

    if (!userId || !userDTO) {
      toast.error('User ID is missing');
      return;
    }

    let phoneCountryCode = '';
let mobileNumber = '';

if (formData.mobile) {
  const phone = parsePhoneNumberFromString(formData.mobile);

  if (phone) {
    phoneCountryCode = `+${phone.countryCallingCode}`;
    mobileNumber = phone.nationalNumber;
  }
}

// VALIDATION: Prevent assigning role if user is inactive
if (formData.status === 'inactive') {
  const originalRole = userDTO?.rolesAssigned || '';

  if (formData.rolesAssigned !== originalRole) {
    toast.error(
      'Cannot assign or change role because the user is inactive. Activate the user first.'
    );
    return;
  }
}
    // Build update payload - role should always be ROLE_USER, rolesAssigned contains selected value
    const updateData: UserDTO = {
      ...userDTO,
      userName: formData.userName,
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      mobile: mobileNumber,                 // ✅ only number
      phoneCountryCode: phoneCountryCode,   // ✅ add this
      roles: ['ROLE_USER'], // Always send ROLE_USER
      rolesAssigned: formData.rolesAssigned, // Send the selected role
      status: formData.status,
      activated: formData.activated,
    };

    console.log('Updating user with data:', updateData);

    updateUserMutation.mutate({ id: parseInt(userId), data: updateData });

    if (formData.password) {
      try {
        await changePasswordMutation.mutate({
            userId: parseInt(userId),
            existingPassword: '',
            newPassword: formData.password,
            confirmPassword: formData.confirmPassword,
        });
        toast.success('User and password updated successfully!');
      } catch (pwError) {
        console.error('Failed to change password:', pwError);
        const pwMsg = (pwError as Error)?.message || 'Please try again.';
        toast.error(`User info updated, but failed to update password: ${pwMsg}`);
        return; 
      }
    } 
  };

  const handleInputChange = (field: keyof EditUserForm, value: string | boolean | string[]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    const currentStatus = formData.status;
    const userIdNum = parseInt(userId);

    // Optimistically update the UI
    setFormData(prev => ({ ...prev, status: newStatus, activated: newStatus === 'active' }));

    // Call the appropriate API
    if (newStatus === 'active') {
      activateUserMutation.mutate(userIdNum);
    } else if (newStatus === 'inactive') {
      deactivateUserMutation.mutate(userIdNum);
    }
  };

 const handleRoleChange = (roleName: string) => {
  if (formData.status === 'inactive') {
    toast.error('Cannot assign role to an inactive user');
    return;
  }

  setFormData(prev => ({
    ...prev,
    roles: ['ROLE_USER'],
    rolesAssigned: roleName
  }));

  if (errors.roles) {
    setErrors(prev => ({ ...prev, roles: '' }));
  }
};

  const getSelectedRoleName = (): string => {
    return formData.rolesAssigned || '';
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'default';
      case 'inactive': return 'secondary';
      case 'suspended': return 'destructive';
      case 'pending': return 'outline';
      default: return 'default';
    }
  };

  const loading = updateUserMutation.loading || changePasswordMutation.loading;

  if (fetchingUser) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/admin/users/list')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Users
          </Button>
          <div>
            <h1 className="text-3xl font-bold">Loading User...</h1>
          </div>
        </div>
      </div>
    );
  }

  if (!userDTO) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/admin/users/list')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Users
          </Button>
          <div>
            <h1 className="text-3xl font-bold">User Not Found</h1>
            <p className="text-muted-foreground">The user you're trying to edit doesn't exist.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 overflow-x-hidden">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/admin/users/list')}
          className="flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Users
        </Button>
        <div>
          <h1 className="text-3xl font-bold">Edit User</h1>
          <p className="text-muted-foreground">
            Update user account information and permissions
          </p>
        </div>
      </div>

      {/* User Info Summary */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>User Summary</span>
            <Badge variant={getStatusColor(formData.status)}>
              {formData.activated ? 'Active' : 'Inactive'}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">User ID:</span>
              <p className="font-mono">{userId}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Username:</span>
              <p>{userDTO.userName}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Last Login:</span>
              <p>{userDTO.lastLoginDate ? formatDate(userDTO.lastLoginDate) : 'N/A'}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Created By:</span>
              <p>{userDTO.createdBy && userDTO.createdBy.toLowerCase() !== 'system' ? userDTO.createdBy : 'Admin'}</p>
            </div>
            <div>
              <span className="text-muted-foreground">Modified By:</span>
              <p>{userDTO.modifiedBy && userDTO.modifiedBy.toLowerCase() !== 'system' ? userDTO.modifiedBy : 'Admin'}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <form onSubmit={handleSubmit}>
        <Card>
          <CardHeader>
            <CardTitle>User Information</CardTitle>
            <CardDescription>
              Update the basic information for the user account
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Username and Name Fields */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label htmlFor="userName" className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Username <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="userName"
                  value={formData.userName}
                  readOnly
                  placeholder="Username"
                  className={`bg-muted cursor-not-allowed ${errors.userName ? 'border-red-500' : ''}`}
                />
                {errors.userName && (
                  <p className="text-sm text-red-500">{errors.userName}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="firstName" className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  First Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="firstName"
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  placeholder="Enter first name"
                  className={errors.firstName ? 'border-red-500' : ''}
                />
                {errors.firstName && (
                  <p className="text-sm text-red-500">{errors.firstName}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="lastName" className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Last Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="lastName"
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  placeholder="Enter last name"
                  className={errors.lastName ? 'border-red-500' : ''}
                />
                {errors.lastName && (
                  <p className="text-sm text-red-500">{errors.lastName}</p>
                )}
              </div>
            </div>

            {/* Email and Phone Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  Email Address <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="email"
                  ref={emailRef}
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  onBlur={async () => {
                    if(userDTO.email === formData.email) return;
                    const dup = await checkEmail(formData.email);
                    if (dup) emailRef.current?.focus();
                  }}
                  placeholder="user@example.com"
                  className={(errors.email || duplicateErrors.email) ? 'border-red-500' : ''}
                />
                {(errors.email || duplicateErrors.email) && (
                  <p className="text-sm text-red-500">{errors.email || duplicateErrors.email}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="mobile" className="flex items-center gap-2">
                  <Phone className="h-4 w-4" />
                  Phone Number <span className="text-red-500">*</span>
                </Label>
                <PhoneInput
                  ref={phoneRef}
                  id="mobile"
                  value={formData.mobile}
                  onChange={(val) => handleInputChange('mobile', val || '')}
                  onBlur={async () => {
                    const rawInput = formData.mobile;                            
                    const parsedInput = parsePhoneNumberFromString(rawInput);
                    const parsedExisting = parsePhoneNumberFromString(userDTO?.mobile || '');
                    const inputNational = parsedInput?.nationalNumber || '';
                    const existingNational = parsedExisting?.nationalNumber || userDTO?.mobile || '';
                    console.log("actual",existingNational)
                    console.log("input",inputNational)
                    if (inputNational === existingNational) return;                            
                    const dup = await checkPhone(inputNational);
                    if (dup) phoneRef.current?.querySelector('input')?.focus();
                  }}
                  hasError={!!(errors.mobile || duplicateErrors.phone)}
                />
                {(errors.mobile || duplicateErrors.phone) && (
                  <p className="text-sm text-red-500">{errors.mobile || duplicateErrors.phone}</p>
                )}
              </div>
            </div>

            {/* Role Field */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="role" className="flex items-center gap-2">
                  <Shield className="h-4 w-4" />
                  Role <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={getSelectedRoleName()}
                  onValueChange={handleRoleChange}
                  disabled={formData.status === 'inactive'}
                >
                  <SelectTrigger className={errors.roles ? 'border-red-500' : ''}>
                    <SelectValue placeholder="Select user role" />
                  </SelectTrigger>
                  <SelectContent>
                    {rolesLoading ? (
                      <SelectItem value="loading" disabled>Loading roles...</SelectItem>
                    ) : rolesDropdown.length === 0 ? (
                      <SelectItem value="no-roles" disabled>No roles available</SelectItem>
                    ) : (
                      rolesDropdown.map((role) => (
                        <SelectItem key={role.roleId} value={role.roleName}>
                          {role.roleName}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                {errors.roles && (
                  <p className="text-sm text-red-500">{errors.roles}</p>
                )}
              </div>
            </div>

            {/* Status and Activation */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="status">Account Status</Label>
                <Select
                  value={formData.status}
                  onValueChange={handleStatusChange}
                  disabled={activateUserMutation.loading || deactivateUserMutation.loading}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>

            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/admin/users/list')}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={loading}>
            <Save className="w-4 h-4 mr-2" />
            {loading ? 'Updating User...' : 'Update User'}
          </Button>
        </div>
      </form>
    </div>
  );
};

export default EditUserPage;
